import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  InputNumber,
  message,
  Table,
  Modal,
  Space,
  Popconfirm,
  ColorPicker,
  Radio,
  Divider,
  Alert,
  Tag,
  Tabs,
  Select,
  DatePicker,
  Checkbox,
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  TrophyOutlined,
  UserOutlined,
  AppstoreOutlined,
  EyeOutlined,
  DatabaseOutlined,
  SyncOutlined,
  ExportOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface ConfigData {
  isEnabled: boolean;
  minWinAmount: number;
  displayCount: number;
  iconColor: string;
  iconType: string;
  title: string;
}

interface UserBlacklistItem {
  id: string;
  userId?: string;
  userAccount: string;
  createdAt: string;
  createdBy: string;
}

interface GameBlacklistItem {
  id: string;
  type: 'venue' | 'game'; // 类型：场馆或子游戏
  venueId?: string;
  venueName?: string; // 游戏场馆名称（如：PP电子、AG真人等）
  gameId?: string;
  gameCode?: string;
  gameName?: string; // 子游戏名称（如果是场馆黑名单，为空）
  gameType?: string; // 游戏类型（真人视讯、体育游戏、彩票游戏、电子游戏）
  createdAt: string;
  createdBy: string;
}

interface DataDetailItem {
  id: string;
  gameId: string;
  gameCode: string;
  gameName: string;
  gameIcon?: string;
  venueId: string;
  venueName: string; // 游戏场馆名称（如：PP电子、AG真人等）
  gameType: string; // 游戏类型（真人视讯、体育游戏、彩票游戏、电子游戏）
  userId: string;
  userAccount: string;
  winAmount: number;
  currency: string;
  winTime: string;
  filterReason: 'normal' | 'user_blacklist' | 'venue_blacklist' | 'game_blacklist' | 'amount_low' | 'demo_account' | 'manual_hide';
  isDisplay: boolean;
  manualOverride?: boolean; // 是否手动设置
}

const JackpotBroadcastConfig: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [userBlacklist, setUserBlacklist] = useState<UserBlacklistItem[]>([]);
  const [gameBlacklist, setGameBlacklist] = useState<GameBlacklistItem[]>([]);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [gameModalVisible, setGameModalVisible] = useState(false);
  const [userForm] = Form.useForm();
  const [gameForm] = Form.useForm();
  const [selectedVenue, setSelectedVenue] = useState<string>('');
  const [venueGames, setVenueGames] = useState<any[]>([]);
  
  // 数据明细相关状态
  const [dataDetails, setDataDetails] = useState<DataDetailItem[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filterForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('config');

  const getCurrentUser = () => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser).name : '管理员';
  };

  // 初始化数据
  useEffect(() => {
    loadConfig();
    loadUserBlacklist();
    loadGameBlacklist();
    if (activeTab === 'details') {
      loadDataDetails();
    }
  }, [activeTab]);

  const loadConfig = async () => {
    try {
      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 100));
      const mockConfig: ConfigData = {
        isEnabled: true,
        minWinAmount: 100,
        displayCount: 20,
        iconColor: '#FFD700',
        iconType: '🎉',
        title: '大奖播报',
      };
      form.setFieldsValue(mockConfig);
    } catch (error) {
      message.error('加载配置失败');
    }
  };

  const loadUserBlacklist = async () => {
    try {
      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 100));
      const mockData: UserBlacklistItem[] = [
        {
          id: '1',
          userAccount: 'demo_user_001',
          createdAt: '2025-01-27 10:00:00',
          createdBy: '管理员',
        },
        {
          id: '2',
          userAccount: 'test_user_002',
          createdAt: '2025-01-27 11:00:00',
          createdBy: '管理员',
        },
      ];
      setUserBlacklist(mockData);
    } catch (error) {
      message.error('加载用户黑名单失败');
    }
  };

  const loadGameBlacklist = async () => {
    try {
      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 100));
      const mockData: GameBlacklistItem[] = [
        {
          id: '1',
          type: 'venue',
          venueId: 'venue_pp',
          venueName: 'PP电子',
          gameType: '电子游戏',
          createdAt: '2025-01-27 10:00:00',
          createdBy: '管理员',
        },
        {
          id: '2',
          type: 'game',
          venueId: 'venue_ag',
          venueName: 'AG真人',
          gameId: 'game_001',
          gameCode: 'baccarat_001',
          gameName: '百家乐',
          gameType: '真人视讯',
          createdAt: '2025-01-27 11:00:00',
          createdBy: '管理员',
        },
      ];
      setGameBlacklist(mockData);
    } catch (error) {
      message.error('加载游戏黑名单失败');
    }
  };

  // 游戏场馆列表（模拟数据）
  const venueList = [
    { id: 'venue_pp', name: 'PP电子', type: '电子游戏' },
    { id: 'venue_ag', name: 'AG真人', type: '真人视讯' },
    { id: 'venue_sexy', name: 'Sexy真人', type: '真人视讯' },
    { id: 'venue_lottery', name: '自营彩票', type: '彩票游戏' },
  ];

  // 根据场馆获取子游戏列表（模拟数据）
  const getGamesByVenue = (venueId: string) => {
    const gamesMap: Record<string, any[]> = {
      venue_pp: [
        { id: 'pp_game_1', code: 'pp_slot_001', name: 'PP老虎机1', type: '电子游戏' },
        { id: 'pp_game_2', code: 'pp_slot_002', name: 'PP老虎机2', type: '电子游戏' },
        { id: 'pp_game_3', code: 'pp_fish_001', name: 'PP捕鱼', type: '电子游戏' },
      ],
      venue_ag: [
        { id: 'ag_game_1', code: 'baccarat_001', name: '百家乐', type: '真人视讯' },
        { id: 'ag_game_2', code: 'dragon_tiger_001', name: '龙虎', type: '真人视讯' },
        { id: 'ag_game_3', code: 'roulette_001', name: '轮盘', type: '真人视讯' },
      ],
      venue_sexy: [
        { id: 'sexy_game_1', code: 'sexy_baccarat_001', name: 'Sexy百家乐', type: '真人视讯' },
        { id: 'sexy_game_2', code: 'sexy_dragon_001', name: 'Sexy龙虎', type: '真人视讯' },
      ],
      venue_lottery: [
        { id: 'lottery_game_1', code: 'lottery_001', name: '时时彩', type: '彩票游戏' },
        { id: 'lottery_game_2', code: 'lottery_002', name: '11选5', type: '彩票游戏' },
      ],
    };
    return gamesMap[venueId] || [];
  };

  const handleSave = async (values: ConfigData) => {
    setLoading(true);
    try {
      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 1000));
      message.success('配置保存成功！');
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    message.info('已重置为默认设置');
  };

  const handleAddUser = async (values: any) => {
    try {
      const newItem: UserBlacklistItem = {
        id: Date.now().toString(),
        userAccount: values.userAccount || values.userId,
        userId: values.userId,
        createdAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        createdBy: getCurrentUser(),
      };
      setUserBlacklist([...userBlacklist, newItem]);
      setUserModalVisible(false);
      userForm.resetFields();
      message.success('添加成功');
    } catch (error) {
      message.error('添加失败');
    }
  };

  const handleDeleteUser = (id: string) => {
    setUserBlacklist(userBlacklist.filter((item) => item.id !== id));
    message.success('删除成功');
  };

  const handleAddGame = async (values: any) => {
    try {
      const venue = venueList.find(v => v.id === values.venueId);
      let newItem: GameBlacklistItem;
      
      if (values.blacklistType === 'venue') {
        // 添加场馆黑名单
        newItem = {
          id: Date.now().toString(),
          type: 'venue',
          venueId: values.venueId,
          venueName: venue?.name || '',
          gameType: venue?.type || '',
          createdAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          createdBy: getCurrentUser(),
        };
      } else {
        // 添加子游戏黑名单
        const game = getGamesByVenue(values.venueId).find(g => g.id === values.gameId);
        newItem = {
          id: Date.now().toString(),
          type: 'game',
          venueId: values.venueId,
          venueName: venue?.name || '',
          gameId: values.gameId,
          gameCode: game?.code || '',
          gameName: game?.name || '',
          gameType: game?.type || venue?.type || '',
          createdAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          createdBy: getCurrentUser(),
        };
      }
      
      setGameBlacklist([...gameBlacklist, newItem]);
      setGameModalVisible(false);
      setSelectedVenue('');
      setVenueGames([]);
      gameForm.resetFields();
      message.success('添加成功');
    } catch (error) {
      message.error('添加失败');
    }
  };

  const handleVenueChange = (venueId: string) => {
    setSelectedVenue(venueId);
    if (venueId) {
      const games = getGamesByVenue(venueId);
      setVenueGames(games);
      // 如果选择的是场馆黑名单，清空子游戏选择
      if (gameForm.getFieldValue('blacklistType') === 'venue') {
        gameForm.setFieldsValue({ gameId: undefined });
      }
    } else {
      setVenueGames([]);
    }
  };

  const handleDeleteGame = (id: string) => {
    setGameBlacklist(gameBlacklist.filter((item) => item.id !== id));
    message.success('删除成功');
  };

  // 数据明细相关函数
  const loadDataDetails = async () => {
    setDataLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      // 模拟数据：包含各种过滤状态的数据
      const mockData: DataDetailItem[] = [
        {
          id: '1',
          gameId: 'ag_game_1',
          gameCode: 'baccarat_001',
          gameName: '百家乐',
          gameIcon: 'https://picsum.photos/64?1',
          venueId: 'venue_ag',
          venueName: 'AG真人',
          gameType: '真人视讯',
          userId: 'user_001',
          userAccount: 'abc***123',
          winAmount: 1234.56,
          currency: 'CNY',
          winTime: dayjs().subtract(5, 'minute').format('YYYY-MM-DD HH:mm:ss'),
          filterReason: 'normal',
          isDisplay: true,
        },
        {
          id: '2',
          gameId: 'pp_game_1',
          gameCode: 'pp_slot_001',
          gameName: 'PP老虎机1',
          gameIcon: 'https://picsum.photos/64?2',
          venueId: 'venue_pp',
          venueName: 'PP电子',
          gameType: '电子游戏',
          userId: 'user_002',
          userAccount: 'demo***456',
          winAmount: 2345.67,
          currency: 'CNY',
          winTime: dayjs().subtract(10, 'minute').format('YYYY-MM-DD HH:mm:ss'),
          filterReason: 'user_blacklist',
          isDisplay: false,
        },
        {
          id: '3',
          gameId: 'ag_game_1',
          gameCode: 'baccarat_001',
          gameName: '百家乐',
          gameIcon: 'https://picsum.photos/64?3',
          venueId: 'venue_ag',
          venueName: 'AG真人',
          gameType: '真人视讯',
          userId: 'user_003',
          userAccount: 'def***789',
          winAmount: 3456.78,
          currency: 'CNY',
          winTime: dayjs().subtract(15, 'minute').format('YYYY-MM-DD HH:mm:ss'),
          filterReason: 'venue_blacklist',
          isDisplay: false,
        },
        {
          id: '4',
          gameId: 'sexy_game_1',
          gameCode: 'sexy_baccarat_001',
          gameName: 'Sexy百家乐',
          gameIcon: 'https://picsum.photos/64?4',
          venueId: 'venue_sexy',
          venueName: 'Sexy真人',
          gameType: '真人视讯',
          userId: 'user_004',
          userAccount: 'ghi***012',
          winAmount: 50.00,
          currency: 'CNY',
          winTime: dayjs().subtract(20, 'minute').format('YYYY-MM-DD HH:mm:ss'),
          filterReason: 'amount_low',
          isDisplay: false,
        },
        {
          id: '5',
          gameId: 'pp_game_2',
          gameCode: 'pp_slot_002',
          gameName: 'PP老虎机2',
          gameIcon: 'https://picsum.photos/64?5',
          venueId: 'venue_pp',
          venueName: 'PP电子',
          gameType: '电子游戏',
          userId: 'demo_user_001',
          userAccount: 'demo***001',
          winAmount: 5678.90,
          currency: 'CNY',
          winTime: dayjs().subtract(25, 'minute').format('YYYY-MM-DD HH:mm:ss'),
          filterReason: 'demo_account',
          isDisplay: false,
        },
        {
          id: '6',
          gameId: 'lottery_game_1',
          gameCode: 'lottery_001',
          gameName: '时时彩',
          gameIcon: 'https://picsum.photos/64?6',
          venueId: 'venue_lottery',
          venueName: '自营彩票',
          gameType: '彩票游戏',
          userId: 'user_005',
          userAccount: 'jkl***345',
          winAmount: 4567.89,
          currency: 'CNY',
          winTime: dayjs().subtract(30, 'minute').format('YYYY-MM-DD HH:mm:ss'),
          filterReason: 'game_blacklist',
          isDisplay: false,
        },
        {
          id: '7',
          gameId: 'ag_game_2',
          gameCode: 'dragon_tiger_001',
          gameName: '龙虎',
          gameIcon: 'https://picsum.photos/64?7',
          venueId: 'venue_ag',
          venueName: 'AG真人',
          gameType: '真人视讯',
          userId: 'user_006',
          userAccount: 'mno***678',
          winAmount: 6789.01,
          currency: 'CNY',
          winTime: dayjs().subtract(35, 'minute').format('YYYY-MM-DD HH:mm:ss'),
          filterReason: 'manual_hide',
          isDisplay: false,
          manualOverride: true,
        },
      ];
      setDataDetails(mockData);
    } catch (error) {
      message.error('加载数据明细失败');
    } finally {
      setDataLoading(false);
    }
  };

  const handleToggleDisplay = async (id: string, isDisplay: boolean) => {
    try {
      setDataDetails(dataDetails.map(item => 
        item.id === id 
          ? { ...item, isDisplay: !isDisplay, manualOverride: true, filterReason: isDisplay ? 'manual_hide' : 'normal' }
          : item
      ));
      message.success(isDisplay ? '已设置为不展示' : '已设置为展示');
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleBatchToggleDisplay = (isDisplay: boolean) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要操作的数据');
      return;
    }
    setDataDetails(dataDetails.map(item => 
      selectedRowKeys.includes(item.id)
        ? { ...item, isDisplay, manualOverride: true, filterReason: isDisplay ? 'normal' : 'manual_hide' }
        : item
    ));
    setSelectedRowKeys([]);
    message.success(`已批量${isDisplay ? '设置为展示' : '设置为不展示'}`);
  };

  const getFilterReasonText = (reason: string) => {
    const reasonMap: Record<string, { text: string; color: string }> = {
      normal: { text: '正常', color: 'green' },
      user_blacklist: { text: '用户黑名单', color: 'red' },
      venue_blacklist: { text: '游戏场馆黑名单', color: 'red' },
      game_blacklist: { text: '游戏黑名单', color: 'red' },
      amount_low: { text: '金额不足', color: 'orange' },
      demo_account: { text: '试玩账号', color: 'purple' },
      manual_hide: { text: '手动隐藏', color: 'default' },
    };
    return reasonMap[reason] || { text: '未知', color: 'default' };
  };

  const userColumns = [
    {
      title: '用户ID/账号',
      dataIndex: 'userAccount',
      key: 'userAccount',
      render: (text: string, record: UserBlacklistItem) => (
        <span>{record.userId || text}</span>
      ),
    },
    {
      title: '添加时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: '操作人',
      dataIndex: 'createdBy',
      key: 'createdBy',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: UserBlacklistItem) => (
        <Popconfirm
          title="确定要删除吗？"
          onConfirm={() => handleDeleteUser(record.id)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const gameColumns = [
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: string) => (
        <Tag color={type === 'venue' ? 'red' : 'orange'}>
          {type === 'venue' ? '场馆' : '子游戏'}
        </Tag>
      ),
    },
    {
      title: '游戏场馆',
      dataIndex: 'venueName',
      key: 'venueName',
      width: 120,
    },
    {
      title: '游戏名称',
      dataIndex: 'gameName',
      key: 'gameName',
      width: 150,
      render: (text: string, record: GameBlacklistItem) => (
        <span>{record.type === 'venue' ? '全部' : (text || '-')}</span>
      ),
    },
    {
      title: '游戏类型',
      dataIndex: 'gameType',
      key: 'gameType',
      width: 100,
      render: (type: string) => (
        <Tag>{type || '-'}</Tag>
      ),
    },
    {
      title: '游戏ID/代码',
      key: 'gameCode',
      width: 150,
      render: (_: any, record: GameBlacklistItem) => (
        <span>{record.type === 'game' ? (record.gameCode || record.gameId || '-') : '-'}</span>
      ),
    },
    {
      title: '添加时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
    },
    {
      title: '操作人',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 100,
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_: any, record: GameBlacklistItem) => (
        <Popconfirm
          title="确定要删除吗？"
          onConfirm={() => handleDeleteGame(record.id)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  // 数据明细表格列定义
  const dataDetailColumns = [
    {
      title: '游戏信息',
      key: 'game',
      width: 250,
      render: (_: any, record: DataDetailItem) => (
        <Space>
          {record.gameIcon && (
            <img src={record.gameIcon} alt={record.gameName} style={{ width: 32, height: 32, borderRadius: 4 }} />
          )}
          <div>
            <div style={{ fontWeight: 500 }}>{record.gameName}</div>
            <div style={{ fontSize: 12, color: '#999' }}>
              {record.venueName} · {record.gameCode}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: '游戏类型',
      dataIndex: 'gameType',
      key: 'gameType',
      width: 100,
      render: (type: string) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: '用户信息',
      key: 'user',
      width: 150,
      render: (_: any, record: DataDetailItem) => (
        <div>
          <div>{record.userAccount}</div>
          <div style={{ fontSize: 12, color: '#999' }}>ID: {record.userId}</div>
        </div>
      ),
    },
    {
      title: '中奖金额',
      key: 'amount',
      width: 150,
      render: (_: any, record: DataDetailItem) => (
        <div>
          <div style={{ fontWeight: 500, color: '#f5222d' }}>
            {record.winAmount.toLocaleString()} {record.currency}
          </div>
        </div>
      ),
    },
    {
      title: '中奖时间',
      dataIndex: 'winTime',
      key: 'winTime',
      width: 180,
    },
    {
      title: '过滤状态',
      dataIndex: 'filterReason',
      key: 'filterReason',
      width: 120,
      render: (reason: string, record: DataDetailItem) => {
        const reasonInfo = getFilterReasonText(reason);
        return (
          <Tag color={reasonInfo.color}>
            {reasonInfo.text}
            {record.manualOverride && ' (手动)'}
          </Tag>
        );
      },
    },
    {
      title: '展示状态',
      dataIndex: 'isDisplay',
      key: 'isDisplay',
      width: 100,
      render: (isDisplay: boolean, record: DataDetailItem) => (
        <Switch
          checked={isDisplay}
          onChange={() => handleToggleDisplay(record.id, isDisplay)}
          checkedChildren="展示"
          unCheckedChildren="不展示"
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24, position: 'relative', zIndex: 1 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h2>
            <TrophyOutlined /> 大奖播报配置
          </h2>
          <p style={{ color: '#666', marginTop: 8 }}>
            配置大奖播报模块的各项参数，包括基础设置、黑名单管理和数据明细
          </p>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'config',
              label: (
                <span>
                  <SettingOutlined /> 基础配置
                </span>
              ),
              children: (
                <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            isEnabled: true,
            minWinAmount: 100,
            displayCount: 20,
            iconColor: '#FFD700',
            iconType: '🎉',
            title: '大奖播报',
          }}
        >
          {/* 基础设置 */}
          <Card
            title="基础设置"
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              <Tag color="blue">更新频率：5分钟（固定，不可配置）</Tag>
            }
          >
            <Form.Item
              label="模块开关"
              name="isEnabled"
              valuePropName="checked"
              tooltip="关闭时，前端不显示悬浮图标，API不返回数据"
            >
              <Switch checkedChildren="开启" unCheckedChildren="关闭" />
            </Form.Item>

            <Form.Item
              label="中奖金额阈值（元）"
              name="minWinAmount"
              tooltip="仅展示中奖金额 ≥ 阈值的记录"
              rules={[
                { required: true, message: '请输入中奖金额阈值' },
                { type: 'number', min: 10, max: 10000, message: '范围：10-10000' },
              ]}
            >
              <InputNumber
                min={10}
                max={10000}
                style={{ width: '100%' }}
                placeholder="请输入中奖金额阈值"
              />
            </Form.Item>

            <Form.Item
              label="展示数量（条）"
              name="displayCount"
              tooltip="弹窗中最多展示的记录数"
              rules={[
                { required: true, message: '请输入展示数量' },
                { type: 'number', min: 10, max: 50, message: '范围：10-50' },
              ]}
            >
              <InputNumber
                min={10}
                max={50}
                style={{ width: '100%' }}
                placeholder="请输入展示数量"
              />
            </Form.Item>
          </Card>

          {/* 用户黑名单 */}
          <Card
            title="用户黑名单"
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setUserModalVisible(true)}
              >
                添加用户
              </Button>
            }
          >
            <Alert
              message="添加到黑名单的用户，其产生的中奖数据将不展示"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Table
              columns={userColumns}
              dataSource={userBlacklist}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>

          {/* 游戏黑名单 */}
          <Card
            title="游戏黑名单"
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setGameModalVisible(true)}
              >
                添加游戏
              </Button>
            }
          >
            <Alert
              message="添加到黑名单的游戏，该游戏的中奖数据将不展示"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Table
              columns={gameColumns}
              dataSource={gameBlacklist}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>

          {/* 预览设置 */}
          <Card title="预览设置" size="small" style={{ marginBottom: 16 }}>
            <Form.Item
              label="悬浮图标颜色"
              name="iconColor"
              tooltip="设置悬浮图标的背景颜色"
            >
              <ColorPicker
                showText
                format="hex"
                style={{ width: 300 }}
              />
            </Form.Item>

            <Form.Item
              label="悬浮图标图标"
              name="iconType"
              tooltip="选择悬浮图标显示的图标"
            >
              <Radio.Group>
                <Radio.Button value="🎉">🎉 庆祝</Radio.Button>
                <Radio.Button value="💰">💰 金币</Radio.Button>
                <Radio.Button value="🏆">🏆 奖杯</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              label="弹窗标题"
              name="title"
              tooltip="设置弹窗显示的标题"
              rules={[{ required: true, message: '请输入弹窗标题' }]}
            >
              <Input placeholder="请输入弹窗标题" maxLength={50} />
            </Form.Item>
          </Card>

          {/* 操作按钮 */}
          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={loading}
              >
                保存配置
              </Button>
            </Space>
          </div>
        </Form>
              ),
            },
            {
              key: 'details',
              label: (
                <span>
                  <DatabaseOutlined /> 数据明细
                </span>
              ),
              children: (
                <div>
                  <Alert
                    message="数据明细说明"
                    description="展示所有进入统计的中奖数据，包括被黑名单过滤的数据。可以手动控制每条记录的展示/不展示状态。"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  
                  {/* 筛选条件 */}
                  <Card size="small" style={{ marginBottom: 16 }}>
                    <Form form={filterForm} layout="inline">
                      <Form.Item label="时间范围" name="timeRange">
                        <RangePicker
                          showTime
                          format="YYYY-MM-DD HH:mm:ss"
                          style={{ width: 400 }}
                        />
                      </Form.Item>
                      <Form.Item label="过滤状态" name="filterReason">
                        <Select placeholder="全部" style={{ width: 180 }} allowClear>
                          <Option value="normal">正常</Option>
                          <Option value="user_blacklist">用户黑名单</Option>
                          <Option value="venue_blacklist">游戏场馆黑名单</Option>
                          <Option value="game_blacklist">游戏黑名单</Option>
                          <Option value="amount_low">金额不足</Option>
                          <Option value="demo_account">试玩账号</Option>
                          <Option value="manual_hide">手动隐藏</Option>
                        </Select>
                      </Form.Item>
                      <Form.Item label="展示状态" name="isDisplay">
                        <Select placeholder="全部" style={{ width: 120 }} allowClear>
                          <Option value={true}>展示中</Option>
                          <Option value={false}>不展示</Option>
                        </Select>
                      </Form.Item>
                      <Form.Item>
                        <Space>
                          <Button type="primary" onClick={loadDataDetails}>
                            查询
                          </Button>
                          <Button onClick={() => filterForm.resetFields()}>
                            重置
                          </Button>
                        </Space>
                      </Form.Item>
                    </Form>
                  </Card>

                  {/* 批量操作 */}
                  <div style={{ marginBottom: 16 }}>
                    <Space>
                      <Button
                        type="primary"
                        onClick={() => handleBatchToggleDisplay(true)}
                        disabled={selectedRowKeys.length === 0}
                      >
                        批量设置为展示
                      </Button>
                      <Button
                        onClick={() => handleBatchToggleDisplay(false)}
                        disabled={selectedRowKeys.length === 0}
                      >
                        批量设置为不展示
                      </Button>
                      <Button icon={<SyncOutlined />} onClick={loadDataDetails}>
                        刷新
                      </Button>
                      <Button icon={<ExportOutlined />}>
                        导出
                      </Button>
                      <span style={{ color: '#999', marginLeft: 8 }}>
                        已选择 {selectedRowKeys.length} 条
                      </span>
                    </Space>
                  </div>

                  {/* 数据表格 */}
                  <Table
                    columns={dataDetailColumns}
                    dataSource={dataDetails}
                    rowKey="id"
                    loading={dataLoading}
                    rowSelection={{
                      selectedRowKeys,
                      onChange: setSelectedRowKeys,
                    }}
                    pagination={{
                      total: dataDetails.length,
                      pageSize: 20,
                      showSizeChanger: true,
                      showTotal: (total) => `共 ${total} 条`,
                    }}
                    scroll={{ x: 1200 }}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* 添加用户黑名单弹窗 */}
      <Modal
        title="添加用户黑名单"
        open={userModalVisible}
        onCancel={() => {
          setUserModalVisible(false);
          userForm.resetFields();
        }}
        onOk={() => userForm.submit()}
        okText="确定"
        cancelText="取消"
        maskClosable={true}
        destroyOnClose={true}
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={handleAddUser}
        >
          <Form.Item
            label="用户ID或账号"
            name="userAccount"
            tooltip="输入用户ID或用户账号"
            rules={[{ required: true, message: '请输入用户ID或账号' }]}
          >
            <Input placeholder="请输入用户ID或账号" />
          </Form.Item>
          <Form.Item
            label="用户ID（可选）"
            name="userId"
            tooltip="如果已知用户ID，可单独填写"
          >
            <Input placeholder="请输入用户ID（可选）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加游戏黑名单弹窗 */}
      <Modal
        title="添加游戏黑名单"
        open={gameModalVisible}
        onCancel={() => {
          setGameModalVisible(false);
          setSelectedVenue('');
          setVenueGames([]);
          gameForm.resetFields();
        }}
        onOk={() => gameForm.submit()}
        okText="确定"
        cancelText="取消"
        width={600}
        maskClosable={true}
        destroyOnClose={true}
      >
        <Form
          form={gameForm}
          layout="vertical"
          onFinish={handleAddGame}
          initialValues={{ blacklistType: 'game' }}
        >
          <Form.Item
            label="黑名单类型"
            name="blacklistType"
            tooltip="选择添加场馆黑名单或子游戏黑名单"
            rules={[{ required: true, message: '请选择黑名单类型' }]}
          >
            <Radio.Group>
              <Radio value="venue">场馆黑名单（该场馆下所有子游戏）</Radio>
              <Radio value="game">子游戏黑名单（仅该子游戏）</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="游戏场馆"
            name="venueId"
            tooltip="选择游戏场馆"
            rules={[{ required: true, message: '请选择游戏场馆' }]}
          >
            <Select
              placeholder="请选择游戏场馆"
              onChange={handleVenueChange}
              allowClear
            >
              {venueList.map(venue => (
                <Option key={venue.id} value={venue.id}>
                  {venue.name} ({venue.type})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.blacklistType !== currentValues.blacklistType ||
              prevValues.venueId !== currentValues.venueId
            }
          >
            {({ getFieldValue }) => {
              const blacklistType = getFieldValue('blacklistType');
              const venueId = getFieldValue('venueId');
              
              if (blacklistType === 'game' && venueId) {
                return (
                  <Form.Item
                    label="子游戏"
                    name="gameId"
                    tooltip="选择具体的子游戏"
                    rules={[{ required: true, message: '请选择子游戏' }]}
                  >
                    <Select placeholder="请先选择游戏场馆，再选择子游戏">
                      {venueGames.map(game => (
                        <Option key={game.id} value={game.id}>
                          {game.name} ({game.code})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default JackpotBroadcastConfig;

